import { Button, DropdownSelect, Modal } from '@components/common';
import { DatePicker } from '@components/common/DatePicker';
import { FormInput, FormTextarea } from '@components/common/FormField';
import { useInventoryItems } from '@hooks/inventory';
import { Plus, Trash2 } from 'lucide-react';
import React, { useDeferredValue, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  InventoryItemRecord,
  InventoryStockLocationRecord,
  InventorySupplierRecord,
  InventoryUnitRecord,
} from '@/renderer/types/inventory';
import { fetchInventoryItems } from '@/renderer/services/inventoryItemService';
import {
  applyItemToLine,
  computeInvoiceTotals,
  createEmptyInvoiceLine,
  INVOICE_STATUS_LABELS,
  recalculateLine,
  SupplierInvoiceFormState,
  SupplierInvoiceLineFormState,
} from './supplierInvoiceForm';

interface SupplierInvoiceEditorModalProps {
  companyId: number;
  initial: SupplierInvoiceFormState;
  isPosted?: boolean;
  isSaving: boolean;
  isPosting: boolean;
  title: string;
  suppliers: InventorySupplierRecord[];
  locations: InventoryStockLocationRecord[];
  units: InventoryUnitRecord[];
  onClose: () => void;
  onSaveDraft: (form: SupplierInvoiceFormState) => void;
  onPost: (form: SupplierInvoiceFormState) => void;
}

export function SupplierInvoiceEditorModal({
  companyId,
  initial,
  isPosted = false,
  isSaving,
  isPosting,
  title,
  suppliers,
  locations,
  units,
  onClose,
  onSaveDraft,
  onPost,
}: SupplierInvoiceEditorModalProps) {
  const [form, setForm] = useState<SupplierInvoiceFormState>(initial);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const deferredItemSearchQuery = useDeferredValue(itemSearchQuery);
  const [itemPage, setItemPage] = useState(1);
  const [loadedItems, setLoadedItems] = useState<InventoryItemRecord[]>([]);
  const isBusy = isSaving || isPosting;

  const selectedItemIds = Array.from(
    new Set(form.lines.map((line) => line.inventoryItemId).filter(Boolean)),
  );

  const itemOptionsQuery = useInventoryItems({
    companyId,
    page: itemPage,
    pageSize: 20,
    search: deferredItemSearchQuery,
    categoryId: 'all',
    status: 'active',
  });

  const selectedItemsQuery = useQuery({
    queryKey: [
      'inventory',
      'invoice-selected-items',
      companyId,
      selectedItemIds,
    ],
    queryFn: async () => {
      const result = await fetchInventoryItems({
        companyId,
        page: 1,
        pageSize: Math.max(selectedItemIds.length, 1),
        search: '',
        categoryId: 'all',
        status: 'all',
        itemIds: selectedItemIds,
      });

      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Failed to fetch selected invoice items',
        );
      }

      return result.data.data;
    },
    enabled: selectedItemIds.length > 0,
  });

  useEffect(() => {
    setItemPage(1);
    setLoadedItems([]);
  }, [deferredItemSearchQuery]);

  useEffect(() => {
    const nextItems = itemOptionsQuery.data?.data;
    if (!nextItems) return;

    setLoadedItems((previous) => {
      const merged = itemPage === 1 ? nextItems : [...previous, ...nextItems];
      return Array.from(
        new Map(merged.map((item) => [item.id, item])).values(),
      );
    });
  }, [itemOptionsQuery.data?.data, itemPage]);

  const setField = <K extends keyof SupplierInvoiceFormState>(
    key: K,
    value: SupplierInvoiceFormState[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateLine = (
    tempId: string,
    patch: Partial<SupplierInvoiceLineFormState>,
  ) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => {
        if (line.tempId !== tempId) return line;
        const updated = { ...line, ...patch };
        return recalculateLine(updated);
      }),
    }));
  };

  const handleItemSelect = (tempId: string, itemId: string) => {
    const item = [...(selectedItemsQuery.data ?? []), ...loadedItems].find(
      (candidate) => candidate.id === itemId,
    );
    if (!item) return;
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => {
        if (line.tempId !== tempId) return line;
        return applyItemToLine(line, item);
      }),
    }));
  };

  const addLine = () =>
    setForm((prev) => ({
      ...prev,
      lines: [...prev.lines, createEmptyInvoiceLine()],
    }));

  const removeLine = (tempId: string) =>
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((l) => l.tempId !== tempId),
    }));

  const { subtotal, taxAmount, totalAmount } = computeInvoiceTotals(form.lines);

  const supplierOptions = suppliers.map((s) => ({
    value: s.id,
    label: s.name,
  }));
  const locationOptions = locations.map((l) => ({
    value: l.id,
    label: l.name,
  }));
  const knownItems = Array.from(
    new Map(
      [...(selectedItemsQuery.data ?? []), ...loadedItems].map((item) => [
        item.id,
        item,
      ]),
    ).values(),
  );
  const itemOptions = loadedItems.map((i) => ({
    value: i.id,
    label: `${i.name}${i.sku ? ` (${i.sku})` : ''}`,
  }));
  const getKnownItem = (itemId: string) =>
    knownItems.find((item) => item.id === itemId);
  const getKnownItemLabel = (itemId: string) => {
    const item = getKnownItem(itemId);
    if (!item) return undefined;
    return `${item.name}${item.sku ? ` (${item.sku})` : ''}`;
  };

  const getUnitName = (unitId: string | null | undefined) => {
    if (!unitId) return '';
    return units.find((u) => u.id === unitId)?.name ?? '';
  };

  const canPost =
    form.supplierId &&
    form.stockLocationId &&
    form.invoiceDate &&
    form.lines.some(
      (l) => l.inventoryItemId && parseFloat(l.quantityPurchased) > 0,
    );

  return (
    <Modal
      isOpen
      onClose={isBusy ? () => {} : onClose}
      title={title}
      size="full"
    >
      <div className="flex flex-col gap-6">
        {/* Header fields */}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <DropdownSelect
            label="Supplier *"
            value={form.supplierId}
            options={[
              { value: '', label: 'Select supplier…' },
              ...supplierOptions,
            ]}
            onChange={(v) => {
              const supplierId = String(v);
              const supplier = suppliers.find((s) => s.id === supplierId);
              setForm((prev) => {
                const terms = supplier?.payment_terms_days ?? 0;
                if (terms <= 0) return { ...prev, supplierId };
                const base =
                  prev.invoiceDate || new Date().toISOString().split('T')[0];
                const due = new Date(base);
                due.setDate(due.getDate() + terms);
                return {
                  ...prev,
                  supplierId,
                  dueDate: due.toISOString().split('T')[0],
                };
              });
            }}
            disabled={isPosted || isBusy}
          />
          <DropdownSelect
            label="Stock location *"
            value={form.stockLocationId}
            options={[
              { value: '', label: 'Select stock location…' },
              ...locationOptions,
            ]}
            onChange={(v) => setField('stockLocationId', String(v))}
            disabled={isPosted || isBusy}
          />
          <FormInput
            label="Invoice number"
            value={form.invoiceNumber}
            onChange={(e) => setField('invoiceNumber', e.target.value)}
            placeholder="Supplier's invoice ref"
            disabled={isPosted || isBusy}
          />
          <DatePicker
            mode="single"
            label="Invoice date *"
            value={form.invoiceDate}
            onChange={(iso) => setField('invoiceDate', iso)}
          />
          <DatePicker
            mode="single"
            label="Due date"
            value={form.dueDate}
            onChange={(iso) => setField('dueDate', iso)}
          />
        </div>

        {/* Line items */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">Line items</p>
            {!isPosted && (
              <Button
                variant="outline"
                size="sm"
                onClick={addLine}
                disabled={isBusy}
              >
                <Plus size={14} />
                Add line
              </Button>
            )}
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr className="text-left">
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[160px]">
                    Item
                  </th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[80px]">
                    Qty
                  </th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[80px]">
                    Unit
                  </th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[70px]">
                    Conv.
                  </th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[80px]">
                    Stock qty
                  </th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[100px]">
                    Unit cost
                  </th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[65px]">
                    Tax %
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 min-w-[100px]">
                    Total
                  </th>
                  {!isPosted && (
                    <th
                      className="px-3 py-2 min-w-[32px]"
                      aria-label="Actions"
                    />
                  )}
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line) => (
                  <tr key={line.tempId} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      {isPosted ? (
                        <span className="text-gray-700">
                          {getKnownItem(line.inventoryItemId)?.name ?? '—'}
                        </span>
                      ) : (
                        <DropdownSelect
                          value={line.inventoryItemId}
                          options={itemOptions}
                          placeholder="Select item…"
                          searchable
                          searchPlaceholder="Search items"
                          onSearchChange={(value) => {
                            setItemSearchQuery(value);
                            setItemPage(1);
                          }}
                          hasMoreOptions={Boolean(
                            itemOptionsQuery.data?.pagination.hasNextPage,
                          )}
                          isLoadingOptions={itemOptionsQuery.isFetching}
                          onLoadMoreOptions={() => {
                            if (itemOptionsQuery.data?.pagination.hasNextPage) {
                              setItemPage((current) => current + 1);
                            }
                          }}
                          customDisplayLabel={getKnownItemLabel(
                            line.inventoryItemId,
                          )}
                          emptyStateLabel="No items found."
                          disabled={isBusy}
                          onChange={(value) =>
                            handleItemSelect(line.tempId, String(value))
                          }
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isPosted ? (
                        <span>{line.quantityPurchased}</span>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          step="0.0001"
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                          value={line.quantityPurchased}
                          onChange={(e) =>
                            updateLine(line.tempId, {
                              quantityPurchased: e.target.value,
                            })
                          }
                          disabled={isBusy}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs">
                      {getUnitName(line.purchaseUnitId) || '—'}
                    </td>
                    <td className="px-3 py-2">
                      {isPosted ? (
                        <span className="text-gray-500 text-xs">
                          {line.conversionRate}
                        </span>
                      ) : (
                        <input
                          type="number"
                          min="0.000001"
                          step="0.000001"
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                          value={line.conversionRate}
                          onChange={(e) =>
                            updateLine(line.tempId, {
                              conversionRate: e.target.value,
                            })
                          }
                          disabled={isBusy}
                          title="Purchase-to-stock conversion rate"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs">
                      {parseFloat(
                        line.quantityInStockUnit || '0',
                      ).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                    </td>
                    <td className="px-3 py-2">
                      {isPosted ? (
                        <span>
                          {parseFloat(line.unitCost || '0').toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 4,
                            },
                          )}
                        </span>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          step="0.0001"
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                          value={line.unitCost}
                          onChange={(e) =>
                            updateLine(line.tempId, {
                              unitCost: e.target.value,
                            })
                          }
                          disabled={isBusy}
                          placeholder="Per purchase unit"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isPosted ? (
                        <span>{line.taxRate}%</span>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                          value={line.taxRate}
                          onChange={(e) =>
                            updateLine(line.tempId, { taxRate: e.target.value })
                          }
                          disabled={isBusy}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">
                      {parseFloat(line.lineTotal || '0').toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </td>
                    {!isPosted && (
                      <td className="px-3 py-2">
                        {form.lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(line.tempId)}
                            disabled={isBusy}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            aria-label="Remove line"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals + notes */}
        <div className="grid gap-4 md:grid-cols-[1fr_auto]">
          <FormTextarea
            label="Notes"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            placeholder="Optional internal notes about this invoice"
            rows={3}
            disabled={isPosted || isBusy}
          />

          <div className="min-w-[200px] rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm">
            <div className="flex justify-between gap-8 text-gray-600">
              <span>Subtotal</span>
              <span>
                {subtotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="mt-1 flex justify-between gap-8 text-gray-600">
              <span>Tax</span>
              <span>
                {taxAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="mt-2 flex justify-between gap-8 border-t border-gray-200 pt-2 font-semibold text-gray-900">
              <span>Total</span>
              <span>
                {totalAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isBusy}>
            Close
          </Button>

          {isPosted ? (
            <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">
              {INVOICE_STATUS_LABELS[initial.supplierId ? 'posted' : 'draft']}
            </span>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onSaveDraft(form)}
                isLoading={isSaving}
                disabled={isBusy || !form.supplierId || !form.stockLocationId}
              >
                Save draft
              </Button>
              <Button
                onClick={() => onPost(form)}
                isLoading={isPosting}
                disabled={isBusy || !canPost}
                title={
                  canPost
                    ? 'Post invoice: updates stock and locks the invoice'
                    : 'Select supplier, location, and add at least one line with item and quantity'
                }
              >
                Post invoice
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
